import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { SparkleIcon, SpinnerGapIcon, ClockIcon, ArrowSquareOutIcon, LightningIcon } from "@phosphor-icons/react";
import { getClientId } from "../lib/clientId";
import { fetchFreshDrops, setDropVerdict } from "../lib/api";
import { TradeButton } from "../components/TradeButton";
import { HitRateBadge } from "../components/HitRateBadge";
import { markFreshDropsSeen } from "../components/FreshDropWatcher";
import { useSeo } from "../lib/useSeo";
import { fmtUsd } from "../lib/format";
import { shortAddr, timeAgo } from "../lib/solana";

const srcMeta = (s) =>
  s === "pump.fun" ? { label: "PUMP.FUN", color: "#00E599" }
  : s === "sweeper" ? { label: "ON-CHAIN SWEEP", color: "#22D3EE" }
  : { label: "INDEXED", color: "#8A8F98" };

const VERDICTS = [
  { key: "rug", label: "💀 Rugged", active: "border-[#FF3366] bg-[#FF3366]/15 text-[#FF3366]" },
  { key: "flash", label: "🪦 Flash", active: "border-[#F0C000] bg-[#F0C000]/15 text-[#F0C000]" },
  { key: "established", label: "🏛 Established", active: "border-sky-400 bg-sky-400/15 text-sky-300" },
  { key: "runner", label: "🚀 Runner", active: "border-neon bg-neon/15 text-neon" },
];

const VerdictRow = ({ d, onSet }) => (
  <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-white/5">
    <span className="text-[9px] font-mono uppercase text-muted-foreground/60 mr-0.5">Tag it</span>
    {VERDICTS.map((v) => {
      const on = d.verdict === v.key;
      return (
        <button
          key={v.key}
          data-testid={`verdict-${v.key}-${d.mint}`}
          onClick={() => onSet(d, on ? null : v.key)}
          title={v.key === "established" ? "Marks it an old coin — removes it from Fresh Drops" : v.key === "runner" ? "Credits the dev's track record with a runner" : v.key === "rug" ? "Adds to the dev's 💀 rug count" : "Lived minutes — flags a flop"}
          className={`text-[10px] font-mono px-1.5 py-0.5 border transition-colors ${on ? v.active : "border-white/10 text-muted-foreground hover:text-white hover:border-white/30"}`}
        >
          {v.label}
        </button>
      );
    })}
  </div>
);

const DropCard = ({ d, onSet }) => {
  const coin = { address: d.mint, symbol: d.symbol, creator: d.creator, url: d.url };
  const src = srcMeta(d.source);
  const fresh = d.ts && Date.now() / 1000 - d.ts < 3600;
  return (
    <div
      data-testid={`drop-card-${d.mint}`}
      className="border bg-card p-4 space-y-3 transition-colors hover:border-white/25"
      style={{ borderColor: fresh ? "rgba(0,229,153,0.5)" : "rgba(255,255,255,0.1)" }}
    >
      <div className="flex items-start gap-3">
        {d.image ? (
          <img src={d.image} alt={d.symbol} className="w-11 h-11 object-cover border border-white/10 shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div className="w-11 h-11 flex items-center justify-center bg-white/5 border border-white/10 font-mono text-xs text-muted-foreground shrink-0">
            {(d.symbol || "?").slice(0, 3)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={`/coin/${d.mint}`} className="font-heading font-bold text-base truncate hover:text-neon transition-colors">
              ${(d.symbol || shortAddr(d.mint)).toUpperCase()}
            </Link>
            {fresh && <span className="font-mono text-[9px] px-1.5 py-0.5 font-bold animate-pulse bg-neon/15 border border-neon text-neon">NEW</span>}
            {!d.live && <span className="font-mono text-[9px] px-1.5 py-0.5 border border-[#F0C000]/60 text-[#F0C000]">PRE-DEX</span>}
          </div>
          <div className="text-xs text-muted-foreground truncate">{d.name || shortAddr(d.mint)}</div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <a href={`https://solscan.io/account/${d.creator}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[11px] text-white/80 hover:text-neon transition-colors">
              {d.devLabel || shortAddr(d.creator)}
            </a>
            <HitRateBadge stats={{ totalTokens: d.totalTokens, runnerCount: d.runnerCount, strongRunnerCount: d.strongRunnerCount, successRate: d.successRate, proven: d.runnerCount > 0 }} compact />
            <span className="font-mono text-[9px] px-1.5 py-0.5" style={{ background: `${src.color}14`, border: `1px solid ${src.color}55`, color: src.color }}>{src.label}</span>
          </div>
        </div>
        <span className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground shrink-0" data-testid={`drop-age-${d.mint}`}>
          <ClockIcon size={11} /> {d.ts ? timeAgo(d.ts) : "—"}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {d.marketCap != null && <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">MCap {fmtUsd(d.marketCap)}</span>}
        {d.liquidity != null && <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">Liq {fmtUsd(d.liquidity)}</span>}
        {d.vol24h != null && <span className="font-mono text-[10px] px-2 py-1 border border-white/10 text-muted-foreground">24h Vol {fmtUsd(d.vol24h)}</span>}
      </div>

      <div className="flex items-center gap-2 pt-1">
        <TradeButton coin={coin} size="sm" />
        <Link to={`/coin/${d.mint}`} data-testid={`drop-detail-${d.mint}`} className="flex items-center gap-1.5 px-3 py-1.5 border border-white/15 text-xs font-semibold text-muted-foreground hover:text-white hover:border-white/30 transition-colors">
          <ArrowSquareOutIcon size={13} /> Details
        </Link>
      </div>

      <VerdictRow d={d} onSet={onSet} />
    </div>
  );
};

export default function FreshDrops() {
  useSeo({
    title: "Fresh Drops — New Launches From Your Saved Devs | AlphaApeing",
    description: "Every brand-new token from the deployers you follow — who launched it, their hit rate, live stats and one-tap buy.",
  });
  const clientId = getClientId();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["freshDrops", clientId],
    queryFn: () => fetchFreshDrops(clientId),
    refetchInterval: 60000,
  });
  const drops = data?.drops || [];

  useEffect(() => {
    markFreshDropsSeen();
    qc.invalidateQueries({ queryKey: ["freshDropsLatest"] });
  }, [drops.length, qc]);

  const tag = async (d, verdict) => {
    try {
      await setDropVerdict({ mint: d.mint, creator: d.creator, verdict });
      qc.setQueryData(["freshDrops", clientId], (old) => old && {
        ...old,
        drops: verdict === "established"
          ? old.drops.filter((x) => x.mint !== d.mint)
          : old.drops.map((x) => (x.mint === d.mint ? { ...x, verdict } : x)),
      });
      qc.invalidateQueries({ queryKey: ["deployerStatsBatch"] });
      toast.success(
        verdict === "established" ? "Marked established — removed from Fresh Drops"
        : verdict === "runner" ? "🚀 Credited to the dev's track record"
        : verdict === "rug" ? "💀 Added to the dev's rug count"
        : verdict === "flash" ? "🪦 Tagged as a flash"
        : "Tag cleared"
      );
    } catch {
      toast.error("Couldn't save the tag");
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="fresh-drops-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><SparkleIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">FRESH <span className="text-neon">DROPS</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl leading-relaxed">
          Every <span className="text-white font-semibold">brand-new launch (last 72h)</span> from the devs you saved — followed builders, Alpha creators and watched wallets —
          with <span className="text-neon">who launched it, their 🎯 hit rate</span>, live stats and a one-tap buy on your preferred exchange.
          Fed by the instant pump.fun stream <span className="text-white">plus an on-chain sweeper</span> that checks your devs' wallets every few minutes so launches on other launchpads can't slip past.
          {data?.devCount != null && <span className="font-mono text-xs"> · watching {data.devCount} devs</span>}
        </p>
      </div>

      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-24 text-muted-foreground gap-3">
          <SpinnerGapIcon size={32} className="animate-spin text-neon" />
          <span className="font-mono text-sm">Checking your devs' wallets…</span>
        </div>
      ) : drops.length === 0 ? (
        <div className="border border-white/10 bg-card py-20 flex flex-col items-center gap-4 text-center px-6" data-testid="fresh-drops-empty">
          <SparkleIcon size={40} className="text-muted-foreground" />
          <p className="text-muted-foreground font-mono text-sm max-w-lg leading-relaxed">
            No fresh launches from your saved devs in the last 72 hours. The on-chain sweeper is checking their wallets
            every few minutes — the moment one mints, it lands here and pings your Telegram.
            Follow more proven devs from the <Link to="/leaderboard" className="text-neon hover:underline">Leaderboard</Link> to widen the net.
          </p>
        </div>
      ) : (
        <div data-testid="fresh-drops-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {drops.map((d) => <DropCard key={d.mint} d={d} onSet={tag} />)}
        </div>
      )}

      <div className="border border-white/10 bg-card p-4 flex gap-3">
        <LightningIcon size={18} className="text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Sources: instant pump.fun websocket + a standard-RPC wallet sweeper (any launchpad, ~5 min lag) + the AlphaApeing token index.
          Pre-DEX tokens show limited stats until they get a live pair. High-risk, high-speed — not financial advice.
        </p>
      </div>
    </div>
  );
}
